import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AtypicalDealsContext } from './contexts/atypical-deals-context';
import { withUBFFContext } from './contexts/ubff-context';

import type { GetPriceBandResponse } from '@shared/types/frontend/ubff/price-band';
import type { UBFFGateway } from '@shared/types/frontend/ubff-gateway';
import type { I18nService } from '@shared/types/i18n';
import type { Stay, Nullable } from '@shared/types/utils';
import type { TaxAndFeeInclusionPolicy } from '@skyscanner-internal/hotel-unified-bff-web-http-client/dist/skyscanner/hotelunifiedbff/common.pb';

import { DATE_FORMAT } from '@shared/utils-universal/i18n/date-formats';

const AtypicalDealsProvider = ({
  baselinePrice,
  children,
  enabled,
  entityId,
  entityType,
  i18n,
  isPollingFinished,
  stay,
  taxAndFeeInclusionPolicy,
  uBFFGateway,
}: {
  children: ReactNode;
  uBFFGateway: UBFFGateway;
  stay: Stay;
  entityId?: string;
  entityType?: string;
  i18n: I18nService;
  enabled: boolean;
  baselinePrice?: number;
  isPollingFinished: boolean;
  taxAndFeeInclusionPolicy?: TaxAndFeeInclusionPolicy;
}) => {
  const { checkIn, checkOut } = stay;
  const { currency, locale, market } = i18n.culture;
  const [response, setResponse] =
    useState<Nullable<GetPriceBandResponse>>(null);
  const [isLoading, setIsLoading] = useState(false);

  const checkinDateStr = i18n.formatDate(
    checkIn,
    DATE_FORMAT.NON_LOCALISED_SHORT,
  );
  const checkoutDateStr = i18n.formatDate(
    checkOut,
    DATE_FORMAT.NON_LOCALISED_SHORT,
  );

  const request = useMemo(
    () => ({
      entityId,
      entityType,
      checkinDate: checkinDateStr,
      checkoutDate: checkoutDateStr,
      currency,
      locale,
      market,
      searchDate: i18n.formatDate(new Date(), DATE_FORMAT.NON_LOCALISED_SHORT),
      taxAndFeeInclusionPolicy,
      baselinePrice: baselinePrice ? `${Math.ceil(baselinePrice)}` : undefined,
    }),
    [
      entityId,
      entityType,
      checkinDateStr,
      checkoutDateStr,
      currency,
      locale,
      market,
      i18n,
      taxAndFeeInclusionPolicy,
      baselinePrice,
    ],
  );

  const fetchResults = useCallback(async () => {
    setIsLoading(true);
    const pbResponse = await uBFFGateway.getPriceBand(request);
    if (pbResponse) {
      setResponse(pbResponse as GetPriceBandResponse);
    } else {
      setResponse(null);
    }
    setIsLoading(false);
  }, [request, uBFFGateway]);

  useEffect(() => {
    const callDealsConditions =
      enabled &&
      isPollingFinished &&
      baselinePrice &&
      stay.numberOfAdults === 2 &&
      stay.numberOfRooms === 1 &&
      stay.childrenAges.length === 0;

    if (callDealsConditions) {
      fetchResults();
    } else {
      setResponse(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchResults,
    stay.numberOfAdults,
    stay.numberOfRooms,
    stay.childrenAges,
  ]);

  const contextValue = useMemo(
    () => ({ response, isLoading }),
    [response, isLoading],
  );

  return (
    <AtypicalDealsContext.Provider value={contextValue}>
      {children}
    </AtypicalDealsContext.Provider>
  );
};

export default withUBFFContext(AtypicalDealsProvider);
